import { TFunction } from 'i18next';

import {
  AuthEventType,
  AuthTerminalCause,
  AuthValidationResult,
  DebugStep,
  DebugStepType,
  SynthesisResponseCode,
} from '@/shared/types/tryIt';

import { ConversationTurn } from '../../../types/conversation';
import { flattenSteps } from '../../../utils/steps';

export type Severity = 'error' | 'warning';

export type TurnIssue = {
  key: string;
  severity: Severity;
  text: string;
  detail?: string;
  webhookId?: string;
};

type IssueSpec = Omit<TurnIssue, 'text' | 'detail'> & {
  labelKey: string;
  labelArgs?: Record<number, string>;
  detailKey?: string;
  detailText?: string;
};

const RESPONSE_CODE_ISSUE: Partial<
  Record<SynthesisResponseCode, { severity: Severity; labelKey: string }>
> = {
  [SynthesisResponseCode.NO_SOURCES]: {
    severity: 'warning',
    labelKey: 'try_it.collection_response_code_2',
  },
  [SynthesisResponseCode.NO_ANSWER]: {
    severity: 'warning',
    labelKey: 'try_it.collection_response_code_3',
  },
  [SynthesisResponseCode.NO_MESSAGE]: {
    severity: 'warning',
    labelKey: 'try_it.collection_response_code_4',
  },
  [SynthesisResponseCode.ERROR]: {
    severity: 'error',
    labelKey: 'try_it.collection_response_code_5',
  },
  [SynthesisResponseCode.NO_ANSWER_REPETITION]: {
    severity: 'warning',
    labelKey: 'try_it.collection_response_code_7',
  },
  [SynthesisResponseCode.NO_ANSWER_LINK_HALLUCINATION]: {
    severity: 'warning',
    labelKey: 'try_it.collection_response_code_8',
  },
  [SynthesisResponseCode.NO_ANSWER_JAILBREAK]: {
    severity: 'warning',
    labelKey: 'try_it.collection_response_code_9',
  },
  [SynthesisResponseCode.NO_ANSWER_FACT_HALLUCINATION]: {
    severity: 'warning',
    labelKey: 'try_it.collection_response_code_10',
  },
  [SynthesisResponseCode.NO_ANSWER_OUT_OF_SCOPE]: {
    severity: 'warning',
    labelKey: 'try_it.collection_response_code_11',
  },
  [SynthesisResponseCode.NO_ANSWER_LENGTH_EXCEEDED]: {
    severity: 'warning',
    labelKey: 'try_it.collection_response_code_12',
  },
  [SynthesisResponseCode.NO_ANSWER_TOOL_HALLUCINATION]: {
    severity: 'warning',
    labelKey: 'try_it.collection_response_code_16',
  },
};

const AUTH_EXIT_ISSUE: Partial<
  Record<AuthTerminalCause, { severity: Severity; labelKey: string }>
> = {
  [AuthTerminalCause.MAX_ATTEMPTS]: {
    severity: 'error',
    labelKey: 'try_it.auth.exit_max_attempts',
  },
  [AuthTerminalCause.TIMEOUT]: { severity: 'error', labelKey: 'try_it.auth.exit_timeout' },
  [AuthTerminalCause.SYSTEM_FAILURE]: {
    severity: 'error',
    labelKey: 'try_it.auth.exit_system_failure',
  },
  [AuthTerminalCause.LLM_EXIT]: { severity: 'warning', labelKey: 'try_it.auth.exit_llm_exit' },
};

const authIssueOf = (step: DebugStep): { severity: Severity; labelKey: string } | null => {
  const auth = step.auth;
  if (!auth) {
    return null;
  }

  if (auth.event === AuthEventType.AUTH_VALIDATOR_SETUP_ERROR) {
    return { severity: 'error', labelKey: 'try_it.turn_issues.auth_validator_error' };
  }

  if (auth.event === AuthEventType.AUTH_VALIDATE_INPUT) {
    if (auth.validation_result === AuthValidationResult.INVALID) {
      return { severity: 'warning', labelKey: 'try_it.turn_issues.auth_invalid' };
    }
    if (auth.validation_result === AuthValidationResult.ERROR) {
      return { severity: 'error', labelKey: 'try_it.turn_issues.auth_validation_error' };
    }
    return null;
  }

  if (
    auth.event === AuthEventType.AUTH_PRE_ENTER_WEBHOOK &&
    auth.validation_result === AuthValidationResult.ERROR
  ) {
    return { severity: 'error', labelKey: 'try_it.turn_issues.auth_webhook_error' };
  }

  if (auth.event === AuthEventType.AUTH_EXIT) {
    if (auth.terminal_cause === AuthTerminalCause.THRESHOLD_MET) {
      return null;
    }
    return (
      (auth.terminal_cause !== undefined && AUTH_EXIT_ISSUE[auth.terminal_cause]) || {
        severity: 'error',
        labelKey: 'try_it.turn_issues.auth_exit_unknown',
      }
    );
  }

  return null;
};

const readableCode = (code?: string) => code?.replace(/[-_]/g, ' ').toLowerCase();

/** One source of truth, so the row's icon and badge never disagree with this summary. */
export const webhookSeverity = (step: DebugStep): Severity | null =>
  step.webhook_error ? 'error' : null;

const webhookIssueOf = (step: DebugStep, key: string): IssueSpec | null => {
  if (!step.webhook_error) {
    return null;
  }

  const fallbackSent = (step.sub_steps ?? []).some((sub) => sub.action_type === 'text');

  return {
    key,
    severity: 'error',
    labelKey: 'try_it.turn_issues.webhook_failed_unnamed',
    webhookId: step.webhook_id,
    detailKey: fallbackSent ? 'try_it.turn_issues.fallback_message_sent' : undefined,
    detailText: fallbackSent
      ? undefined
      : (readableCode(step.webhook_error.code) ??
        (step.webhook_error.status_code ? String(step.webhook_error.status_code) : undefined)),
  };
};

const collectIssues = (turn: ConversationTurn): IssueSpec[] => {
  const issues: IssueSpec[] = [];

  if (turn.metadata.isError) {
    issues.push({
      key: 'internal-error',
      severity: 'error',
      labelKey: 'try_it.answer_internal_error',
    });
  }

  const responseCode = turn.metadata.collection?.response_code as SynthesisResponseCode | undefined;
  const responseIssue = responseCode === undefined ? undefined : RESPONSE_CODE_ISSUE[responseCode];
  if (responseIssue) {
    const servedFallback =
      responseCode === SynthesisResponseCode.ERROR && turn.responses.length > 0;

    issues.push({
      key: 'response-code',
      ...responseIssue,
      severity: servedFallback ? 'warning' : responseIssue.severity,
      detailKey: servedFallback ? 'try_it.turn_issues.fallback_message_sent' : undefined,
    });
  }

  flattenSteps(turn.metadata.steps).forEach((step, index) => {
    const key = `step-${index}`;

    const webhookIssue = webhookIssueOf(step, key);
    if (webhookIssue) {
      issues.push(webhookIssue);
      return;
    }

    if (step.type === DebugStepType.INFINITE_LOOP) {
      issues.push({ key, severity: 'warning', labelKey: 'try_it.details.infinite_loop' });
      return;
    }

    const auth = authIssueOf(step);
    if (auth) {
      issues.push({ key, ...auth });
    }
  });

  (turn.metadata.toolCalls ?? []).forEach((tool, index) => {
    if (!tool.succeeded) {
      issues.push({
        key: `tool-${index}`,
        severity: 'error',
        labelKey: 'try_it.turn_issues.tool_failed',
        labelArgs: { 0: tool.name },
      });
    }
  });

  return issues;
};

export const hasTurnIssue = (turn: ConversationTurn) =>
  turn.metadata.isInterrupted || collectIssues(turn).length > 0;

export const buildTurnIssues = (t: TFunction, turn: ConversationTurn): TurnIssue[] =>
  collectIssues(turn).map(({ labelKey, labelArgs, detailKey, detailText, ...issue }) => ({
    ...issue,
    text: t(labelKey, labelArgs),
    detail: detailKey ? t(detailKey) : detailText,
  }));
